/**
 * `<tem-screen-view [templateId]>` — a tela pronta, do id ao pixel.
 *
 * Encapsula a sequência que hoje está copiada em cada consumidor: carregar,
 * migrar, montar o formulário, desenhar, e mostrar carregando e erro. É o que
 * deixa `screen-launcher` e `template-detail` com apenas o que é deles.
 *
 * Só leitura. Não instancia store, não edita, não grava. É essa separação que
 * garante que nenhum bug do editor alcance tela publicada.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzResultModule } from 'ng-zorro-antd/result';

import { BindingResult, applyDataToForm } from '../runtime/data-binding';
import { ScreenDocument } from '../model/screen-schema';
import { LoadedScreen, ScreenDocumentApiService } from '../persistence/screen-document-api.service';
import { ScreenRendererComponent } from '../render/screen-renderer.component';
import { TranslateTAPipe } from '../render/translate-ta.pipe';
import { ScreenForm, ScreenFormService } from '../runtime/screen-form.service';
import { EventRuntimeService } from '../runtime/event-runtime.service';
import { MasterDetailBinderService } from '../runtime/master-detail-binder.service';
import { RuntimeIssue, SCREEN_RUNTIME } from '../runtime/screen-runtime';
import {
  ScreenComponentRegistry,
  defaultRegistry
} from '../registry/component-registry';

@Component({
  selector: 'tem-screen-view',
  standalone: true,
  imports: [
    CommonModule,
    NzSpinModule,
    NzResultModule,
    ScreenRendererComponent,
    TranslateTAPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Um formulário por tela viva: `simple-datagrid` mantém grade pai e filha
  // abertas ao mesmo tempo, e um serviço em root faria as duas compartilharem
  // controles.
  providers: [
    // O catalogo de tipos. `ScreenComponentRegistry` NAO tem decorador — e
    // TypeScript puro, para o registry seguir testavel sem TestBed — entao
    // precisa ser provido a mao por quem hospeda uma tela. Faltava aqui, e o
    // efeito era `NG0201: No provider found for ScreenComponentRegistry` assim
    // que `ScreenFormService` era criado: o launcher e o `template-detail` nao
    // desenhavam nada.
    //
    // Instancia unica e compartilhada de proposito: o catalogo e imutavel, e uma
    // copia por tela so gastaria memoria.
    { provide: ScreenComponentRegistry, useValue: defaultRegistry },
    ScreenFormService,
    EventRuntimeService,
    // Liga a grade ao painel: linha escolhida vira valores no
    // formulário. Substitui o `master-detail-panel`, que fazia isso
    // procurando `<input>` no DOM.
    MasterDetailBinderService,
    // O `NodeHost` le o estado das regras por este token. Apontar para a MESMA
    // instancia do servico evita duas fontes de verdade sobre a mesma tela.
    { provide: SCREEN_RUNTIME, useExisting: EventRuntimeService }
  ],
  template: `
    @if (loading()) {
      <div class="tem-screen-view__state">
        <nz-spin nzSimple />
      </div>
    } @else if (errorMessage(); as message) {
      <nz-result
        nzStatus="error"
        [nzTitle]="'builderScreen.view.loadFailed' | translateTA"
        [nzSubTitle]="message" />
    } @else if (document(); as doc) {
      <tem-screen-render [document]="doc" [form]="form()" [data]="data()" mode="view" />
    }
  `,
  styles: [`
    :host { display: block; }

    .tem-screen-view__state {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 200px;
    }
  `]
})
export class ScreenViewComponent {

  readonly templateId = input.required<number>();

  /**
   * O dado que preenche a tela.
   *
   * Vem de fora porque quem sabe buscá-lo é o consumidor: o launcher executa a
   * fonte de dados configurada, o `template-detail` recebe a linha do
   * `simple-datagrid`. O motor não escolhe origem de dado — só desenha o que
   * recebe.
   */
  readonly data = input<unknown>(null);

  /**
   * Emitido a cada aplicação de dados, com os caminhos que não existem no JSON.
   *
   * É o insumo do aviso de `dataPath` inexistente na tela "Verificar tela".
   * Hoje esse caso falha em silêncio: o campo aparece vazio e ninguém sabe se
   * o dado não veio ou se o caminho está errado.
   */
  readonly bound = output<BindingResult>();

  /**
   * Problemas que o comportamento encontrou: regra apontando para campo que nao
   * existe, acao apontando para no removido, cadeia longa demais.
   *
   * Hoje tudo isso falha em silencio. Emitir e o que permite a tela
   * "Verificar tela" listar, em vez de o usuario descobrir em producao.
   */
  readonly runtimeIssues = output<readonly RuntimeIssue[]>();

  private readonly api = inject(ScreenDocumentApiService);
  private readonly forms = inject(ScreenFormService);
  private readonly runtime = inject(EventRuntimeService);
  private readonly masterDetail = inject(MasterDetailBinderService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly loaded = signal<LoadedScreen | null>(null);
  private readonly failure = signal<string | null>(null);

  readonly loading = signal(true);

  readonly document = computed<ScreenDocument | null>(() => this.loaded()?.document ?? null);
  readonly errorMessage = computed<string | null>(() => this.failure());

  /**
   * O formulário é montado UMA vez por documento, com os índices junto.
   *
   * `computed` garante isso por identidade: enquanto o documento for o mesmo
   * objeto, o formulário é o mesmo, e o que o usuário digitou não se perde numa
   * detecção de mudanças.
   */
  private readonly builtForm = computed<ScreenForm | null>(() => {
    const doc = this.document();
    if (doc === null) {
      return null;
    }
    return this.forms.buildForm(doc);
  });

  readonly form = computed<FormGroup | null>(() => this.builtForm()?.group ?? null);

  constructor() {
    effect(() => {
      const id = this.templateId();
      this.fetch(id);
    });

    // Preenche assim que o documento OU o dado mudam. Separado da carga de
    // proposito: o dado costuma chegar depois da tela, e trocar o dado nao pode
    // exigir recarregar a definicao da tela do banco.
    effect(() => {
      const doc = this.document();
      const form = this.form();
      const data = this.data();

      if (doc === null || form === null || data === null || data === undefined) {
        return;
      }

      const screenForm = this.builtForm();
      if (screenForm === null) {
        return;
      }

      this.bound.emit(applyDataToForm(doc, screenForm, data));
    });

    // O runtime comeca depois que o formulario existe, e recomeca quando o
    // documento troca. Os dados sao aplicados no efeito acima, com
    // `emitEvent: false`, entao carregar a tela nao dispara o onChange de cada
    // campo — abrir uma tela nao pode executar a logica dela inteira como se o
    // usuario tivesse digitado tudo.
    effect(() => {
      const doc = this.document();
      const screenForm = this.builtForm();

      if (doc === null || screenForm === null) {
        return;
      }

      this.runtime.start(doc, screenForm);
      this.masterDetail.start(doc, screenForm);
      this.runtimeIssues.emit(this.runtime.issues());
    });
  }

  private fetch(idTemplate: number): void {
    this.loading.set(true);
    this.failure.set(null);

    this.api.load(idTemplate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.loaded.set(result);
          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.loaded.set(null);
          this.failure.set(this.describe(error));
          this.loading.set(false);
        }
      });
  }

  private describe(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return '';
  }
}
